import React, { useState, useEffect } from 'react';
import { Activity, ActivityEditorProps } from '../types';
import { ActivityInfo } from '../ActivityCommon';
import ActivityHeaderEditor from './ActivityHeaderEditor';
import RichTextEditor from '@/components/ui/RichTextEditor';
import toast from 'react-hot-toast';

// Lesson Editor Component
const LessonEditor: React.FC<ActivityEditorProps> = ({ activity, onActivityUpdate }) => {
  const [content, setContent] = useState('');
  const [title, setTitle] = useState(activity.title);
  const [description, setDescription] = useState(activity.description || '');

  // Initialize content from activity when component mounts or activity changes
  useEffect(() => {
    if (activity.content) {
      setContent(activity.content);
    }
    setTitle(activity.title);
    setDescription(activity.description || '');
  }, [activity]);

  // Update the activity content when it changes
  useEffect(() => {
    if (onActivityUpdate && (content !== activity.content || title !== activity.title || description !== activity.description)) {
      const updatedActivity = {
        ...activity,
        content: content,
        title: title,
        description: description
      };
      
      // We're not awaiting this to avoid blocking the UI
      // The parent component will handle the saving state
      onActivityUpdate(updatedActivity).catch(error => {
        console.error('Error updating lesson content:', error);
      });
    }
  }, [content, title, description]);

  // Handle AI generated content from parent component
  useEffect(() => {
    const handleAiContent = (event: CustomEvent) => {
      if (event.detail && event.detail.activityId === activity.activity_id) {
        handleAiGeneratedContent(event.detail.content);
      }
    };
    
    window.addEventListener('ai-content-generated' as any, handleAiContent as EventListener);
    
    return () => {
      window.removeEventListener('ai-content-generated' as any, handleAiContent as EventListener);
    };
  }, [activity.activity_id]);

  // Handle AI generated content
  const handleAiGeneratedContent = (generatedContent: any) => {
    console.log('AI generated lesson content:', generatedContent);
    
    if (generatedContent.title) {
      setTitle(generatedContent.title);
    }
    
    if (generatedContent.description) {
      setDescription(generatedContent.description);
    }
    
    if (generatedContent.content) {
      setContent(generatedContent.content);
    } else if (generatedContent.raw_response) {
      toast.error('AI generated content could not be automatically applied. Please try again with a more specific prompt.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Activity Header */}
      <ActivityHeaderEditor
        title={title}
        description={description}
        activityType="Lesson"
        onTitleChange={setTitle}
        onDescriptionChange={setDescription}
        titlePlaceholder="Lesson Title"
        descriptionPlaceholder="Lesson Description"
      />

      <div>
        <h3 className="text-lg font-medium mb-3">Lesson Content</h3>
        <RichTextEditor
          initialContent={content}
          onChange={setContent}
          placeholder="Enter lesson content here..."
          height="400px"
        />
      </div>
    </div>
  );
};

export default LessonEditor; 